<script setup lang="ts">
import { computed, ref } from 'vue';
import { AccordionContent } from '@hfc/ui-core';
import SmallBusinessPersonForm from '@/application/smallBusiness/infrastructure/components/SmallBusinessPersonForm.vue';
import { SmallBusinessEmployee } from '@/application/smallBusiness/domain/SmallBusinessEmployee';
import { SmallBusinessMember } from '@/application/smallBusiness/domain/SmallBusinessMember';
import { Relationship, Relationships } from '@/application/smallBusiness/domain/SmallBusinessRelationships';
import { UUID } from '@hfc/common/src/types/UUID';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import { useForm } from 'vee-validate';
import SmallBusinessAddPersonButton from '@/application/smallBusiness/infrastructure/components/SmallBusinessAddPersonButton.vue';

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();

const props = defineProps<{
  applicant: SmallBusinessEmployee;
  show?: boolean;
}>();

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const smallBusinessSpouseFormRef = ref<InstanceType<typeof SmallBusinessPersonForm> | null>(null);
const smallBusinessPersonFormRef = ref<Array<InstanceType<typeof SmallBusinessPersonForm>>>([]);

const hasErrors = ref(false);
const isVisible = ref(props.show);

const spouseKey = Relationships.Spouse.key as Relationship;
const childKey = Relationships.Child.key as Relationship;

const applicantSpouse = computed(() => props.applicant.dependents.find(dependent => dependent.relationship === Relationships.Spouse.key));
const applicantChildren = computed(() => props.applicant.dependents.filter(dependent => dependent.relationship === Relationships.Child.key) || []);
const showClass = computed(() => props.show ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]');

const isValid = async (): Promise<boolean> => {
  const localValid = await runValidations();
  if (!localValid) {
    hasErrors.value = true;

    return false;
  }
  smallBusinessStore.saveApplicationOnLocalStorage();
  hasErrors.value = false;

  return true;
};

const removeDependent = (id: UUID): void => {
  smallBusinessStore.removeDependent(props.applicant, id);
};

const updateDependent = (value: unknown, field: string, id: UUID): void => {
  const dependent = props.applicant.dependents.find(d => d.id === id);
  if (dependent) {
    smallBusinessStore.updateDependent(props.applicant, { ...dependent, [field]: value } as SmallBusinessMember);
  }
};

const getAddedDependents = (): string => {
  const childrenCount = applicantChildren.value.length;
  const spouseText = applicantSpouse.value ? 'Spouse' : '';
  const childrenText = childrenCount > 0 ? `${childrenCount} Child${childrenCount > 1 ? 'ren' : ''}` : '';

  return [spouseText, childrenText].filter(Boolean).join(', ');
};

const handleTransitionEnd = (): void => {
  if (!props.show) {
    isVisible.value = false;
  }
};

const handleTransitionStart = (): void => {
  if (props.show) {
    isVisible.value = true;
  }
};

defineExpose({
  isValid,
});
</script>
<template>
  <div
    class="grid transition-all duration-300 ease-in-out"
    :class="showClass"
    @transitionstart="handleTransitionStart"
    @transitionend="handleTransitionEnd"
  >
    <div :class="{ 'overflow-hidden': !show }">
      <AccordionContent class="my-4" open-icon="chevron-up" closed-icon="chevron-down" open-icon-color="text-blue-500" closed-icon-color="text-blue-500" :content-first="true" open content-class="pt-0" :class="{ 'border border-blue-400 rounded-md': !hasErrors, 'border border-red-500 rounded-md': hasErrors }">
        <template #title="{ isOpen }">
          <div class="flex w-full justify-end items-center">
            <div class="flex font-semibold text-blue-500">
              {{ getAddedDependents() }}
            </div>
            <div v-if="isOpen" class="flex space-x-4 ml-4">
              <SmallBusinessAddPersonButton :person="applicant" :person-relationship="spouseKey" @click.stop />
              <SmallBusinessAddPersonButton :person="applicant" :person-relationship="childKey" @click.stop />
            </div>
          </div>
        </template>
        <template #content>
          <div class="">
            <SmallBusinessPersonForm
              v-if="applicantSpouse"
              ref="smallBusinessSpouseFormRef"
              label="Spouse" :person="applicantSpouse"
              :index="0"
              :compact="true"
              @remove="removeDependent(applicantSpouse.id)"
              @updatePerson="updateDependent"
              :show-dependents-type="true"
              :auto-focus-date="applicantSpouse.id === smallBusinessState.dependentToFocus"
              :container-class="applicantChildren.length > 0 ? 'border-b-2 border-slate-100' : ''" />
            <SmallBusinessPersonForm
              v-for="(dependent, j) in applicantChildren"
              ref="smallBusinessPersonFormRef"
              :key="j"
              label="Dependent"
              :person="dependent"
              :index="j"
              :compact="true"
              @remove="removeDependent(dependent.id)"
              @updatePerson="updateDependent"
              :auto-focus-date="dependent.id === smallBusinessState.dependentToFocus"
              :show-dependents-type="true"
              :show-index="true"
              :container-class="applicantChildren.length !== j + 1 ? 'border-b-2 border-slate-100' : ''" />
          </div>
        </template>
      </AccordionContent>
    </div>
  </div>
</template>
